import { forwardRef, useState } from 'react';

import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { TextField, Box, IconButton, Tooltip } from '@mui/material';
import { useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import type { MappingRule, InputColor } from '../../dataProvider/types';

interface MappingRuleInputProps {
  mappingRule: MappingRule;
  color?: InputColor;
  onChange: (mappingRule: MappingRule) => void;
  onDelete: () => void;
}

const MappingRuleInput = forwardRef<HTMLInputElement, MappingRuleInputProps>(
  ({ mappingRule, color = 'primary', onChange, onDelete }, ref) => {
    const [matchRuleHelperText, setMatchRuleHelperText] = useState<string>('');
    const [replaceRuleHelperText, setReplaceRuleHelperText] = useState<string>('');
    const translate = useTranslate();

    const handleMatchRule = (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (event.target.value === '') {
        flowchartLogger.debug('Validation error: matchRule is empty');
        setMatchRuleHelperText(translate('callflows.nodes.mapping.mappingRuleErrorEmpty'));
      } else {
        flowchartLogger.debug('Validation success: matchRule is not empty');
        setMatchRuleHelperText('');
      }
      onChange({ ...mappingRule, matchRule: event.target.value });
    };

    const handleReplaceRule = (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (event.target.value === '') {
        flowchartLogger.debug('Validation error: replaceRule is empty');
        setReplaceRuleHelperText(translate('callflows.nodes.mapping.mappingRuleErrorEmpty'));
      } else {
        flowchartLogger.debug('Validation success: replaceRule is not empty');
        setReplaceRuleHelperText('');
      }
      onChange({ ...mappingRule, replaceRule: event.target.value });
    };

    return (
      <Box display='flex' alignItems='start' gap={1} data-testid='mapping-rule-input'>
        <TextField
          inputRef={ref}
          label={translate('callflows.nodes.router.matchRule')}
          value={mappingRule.matchRule}
          color={color}
          focused={color !== 'primary'}
          onChange={handleMatchRule}
          error={matchRuleHelperText !== ''}
          helperText={matchRuleHelperText}
        />
        <TextField
          label={translate('callflows.nodes.mapping.replaceRule')}
          value={mappingRule.replaceRule}
          color={color}
          focused={color !== 'primary'}
          onChange={handleReplaceRule}
          error={replaceRuleHelperText !== ''}
          helperText={replaceRuleHelperText}
        />
        <Tooltip title={translate('ra.action.delete')}>
          <IconButton color='error' onClick={onDelete}>
            <DeleteRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }
);

MappingRuleInput.displayName = 'MappingRuleInput';

export default MappingRuleInput;
